import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SchedulerViewContext } from '../../../context/SchedulerView';
import MultiSelector from '../../tools/MultiSelector';
import ReasonForm from './ReasonForm';

import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { DIALOG_TYPES, getRobotDisplay } from '../Utils';
import ConfirmCancelSessionForm from './ConfirmCancelForm';

export default function CancelSessionForm(props) {
  const {
    isDisabled,
    openModal,
    setOpenModal,
    dateCellSelected,
    selectedSessions,
    inputSelectedRobots,
    actionReasons,
    isLoadingActionReasons,
    getActionReasons,
    onClose,
    setSnackAlert,
    cancelSessions,
    loadingCancelSessionResponse,
    cancelMessage,
    removeCancelSessionResponse,
  } = props;

  const [stepIndex, setStepIndex] = useState({
    RECURRING_SESSION: 0,
    COMMENTS: 1,
    CONFIRM_CANCELATION: 2,
  });
  const [steps, setSteps] = useState(['Recurring Session', 'Comments', 'Confirm Cancelation']);
  const [selectedRobots, setSelectedRobots] = useState([]);
  const [selectedCancelOption, setSelectedCancelOption] = useState('this_session');
  const [confirmRemoveSession, setConfirmRemoveSession] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [openWarning, setOpenWarning] = useState(false);

  const [endDate, setEndDate] = useState(dateCellSelected?.date ? dayjs(dateCellSelected.date) : dayjs());
  const [maxDateForm, setMaxDateForm] = useState(dayjs());
  const [reasonError, setReasonError] = useState(false);

  const reasonForm = useRef({});

  const { t } = useTranslation();

  const {
    dialogType,
    selectedChain,
  } = useContext(SchedulerViewContext);

  // Hooks
  useEffect(() => {
    getActionReasons({ command: dialogType === DIALOG_TYPES.CANCEL ? 'cancel_session' : 'cancel_multiple_sessions' });
    setOpenWarning(false);
    reasonForm.current = {};
    return () => {
      getActionReasons({ command: 'cancel_session' });
      setOpenWarning(false);
    }
  }, []);

  useEffect(() => {
    if (dialogType) {
      getActionReasons({ command: dialogType === DIALOG_TYPES.CANCEL ? 'cancel_session' : 'cancel_multiple_sessions' });
    }
  }, [dialogType]);

  useEffect(() => {
    if (selectedSessions?.length) {
      const isMaxDateNull = selectedSessions.every(session => !session.local_end_date);
      if (!isMaxDateNull) {
        const maxDate = selectedSessions.reduce((acc, session) => {
          const date = dayjs(session.local_end_date, 'YYYY-MM-DD');
          return date.isAfter(acc) ? date : acc;
        }, dayjs());
        setMaxDateForm(maxDate);
      } else {
        setMaxDateForm(null);
      }

      if (handleDisableNoPeriodic()) {
        setSteps(['Comments', 'Confirm Cancelation']);
        setStepIndex({
          COMMENTS: 0,
          CONFIRM_CANCELATION: 1,
        });
      } else {
        setSteps(['Recurring Session', 'Comments', 'Confirm Cancelation']);
        setStepIndex({
          RECURRING_SESSION: 0,
          COMMENTS: 1,
          CONFIRM_CANCELATION: 2,
        });
      }
    }
  }, [selectedSessions]);

  useEffect(() => {
    if (dateCellSelected?.date) {
      setEndDate(dayjs(dateCellSelected.date));
    }
  }, [dateCellSelected]);

  useEffect(() => {
    const selectedRobotsAux = selectedSessions.map(session => {
      return {
        ...inputSelectedRobots.find(robot => robot.robot_uuid === session.robot_uuid),
        sessionType: session.type,
        sessionLocalStartTime: dayjs(session.local_start_time, 'HH:mm:ss A').format('hh:mm A'),
      };
    });
    if (selectedRobotsAux.length) setSelectedRobots(selectedRobotsAux);
    if (openModal) handleStep(0)();
  }, [openModal]);

  useEffect(() => {
    if (!loadingCancelSessionResponse && cancelMessage) {
      if (cancelMessage?.message === 'Success, Session cancelled') {
        const snack = {
          open: true,
          message: `${cancelMessage.data?.length} Session(s) successfully cancelled`,
          severity: 'success',
        };
        setSnackAlert(snack);
        onClose({ canReload: true });
        removeCancelSessionResponse();
      }
    }
  }, [loadingCancelSessionResponse, cancelMessage]);

  // Handlers
  const totalSteps = () => {
    return steps.length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const handleNext = () => {
    if (!isLastStep()) {
      handleStep(activeStep + 1)();
    } else if (isDisabled) {
      handleCloseForm({ canCheckClose: false });
    } else {
      handleCompleteForm();
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      handleCloseForm();
      return;
    }
    handleStep(activeStep - 1)()
  };

  const handleCloseForm = ({ canCheckClose = true } = {}) => {
    const properlyClose = canCheckClose ? window.confirm('Are you sure you want to close the form?') : true;
    if (properlyClose) {
      reasonForm.current = {};
      setOpenModal(false);
    }
  }

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleCompleteForm = () => {
    const {
      comment,
      reason,
    } = reasonForm.current;
    const comments = JSON.stringify({ comment, reason });
    let local_end_date;
    if (selectedCancelOption === 'this_session') {
      local_end_date = dateCellSelected.date;
    } else if (selectedCancelOption === 'this_and_following') {
      local_end_date = null;
    } else if (selectedCancelOption === 'all_sessions_until') {
      local_end_date = endDate.format('YYYY-MM-DD');
    }
    if (!reason) {
      setSnackAlert({
        open: true,
        message: 'Please select a reason',
        severity: 'error',
      });
      setReasonError(true);
      setActiveStep(1);
      return;
    } else {
      const cancelForm = selectedSessions.map(session => {
        return local_end_date ? {
          session_id: session.session_id,
          local_start_date: dateCellSelected.date,
          local_end_date,
          comments
        } : {
          session_id: session.session_id,
          local_start_date: dateCellSelected.date,
          comments,
        }
      })
      cancelSessions({ sessions: cancelForm });
    }
  }

  const handleCheckDisabledConfirm = () => {
    return confirmRemoveSession === 'CANCEL' && activeStep === steps.length - 1 ? false : !(activeStep !== steps.length - 1);
  }

  const handleDisableNoPeriodic = () => {
    return selectedSessions.findIndex(session => session.type !== 'Periodic') !== -1;
  }

  const handleSessionTitle = () => {
    if (selectedSessions.length === 1) {
      return dayjs(selectedSessions[0].local_start_time, 'HH:mm:ss A').format('hh:mm A');
    } else {
      return 'Multiple sessions';
    }
  }

  return (
    <>
      <Dialog open={openModal} maxWidth="lg" width='auto'>
        <DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">Cancel Session {dayjs(dateCellSelected?.date).format('YYYY-MM-DD')} - <b>{handleSessionTitle()}</b></Typography>
            <Tooltip title="Close">
              <IconButton onClick={() => setOpenModal(false)} sx={{ p: 0 }}>
                <IconComponent iconName={'close-circle-outline'} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: `min(100%, calc(${document.getElementById('layout-map-box')?.clientHeight ?? 400}px + 7em))`, overflowY: 'auto', maxHeight: '45em', overflow: 'hidden', pb: 0 }}>
          <Grid container>
            <Grid item xs={12}>
              <Grid container columnSpacing={1} sx={{ pt: 1 }}>
                <Grid item xs={3}>
                  {selectedChain?.length > 0 ?
                    <MultiSelector
                      options={selectedChain.map((chain) => ({ name: chain }))}
                      isDisabled={true}
                      inputSelected={selectedChain}
                      setInputSelectedOptions={() => { }}
                      inputLabel={'Chain'}
                      objectName={'name'}
                      objectId={'name'}
                    />
                    : <Box sx={{ height: '2.5em', border: '1px solid #777', borderRadius: '5px', p: 2 }}> <Typography variant="subtitle2">Chain</Typography></Box>
                  }
                </Grid>
                <Grid item xs={4}>
                  {selectedRobots?.length > 0 &&
                    <>
                      <MultiSelector
                        options={selectedRobots.map((robot) => ({
                          ...robot,
                          robot_display: getRobotDisplay(robot),
                        }))}
                        isDisabled={true}
                        inputSelected={selectedRobots.map(robot => robot.robot_uuid)}
                        setInputSelectedOptions={() => { }}
                        inputLabel={'Robot'}
                        objectName={'robot_display'}
                        objectId={'robot_uuid'}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, m: 0, fontSize: '0.75em' }}>
                        {t('overseer_app.scheduler.robot_code_hint', 'Store - Floor - Robot ID')}
                      </Typography>
                    </>
                  }
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 1 }}>
                <Stepper nonLinear activeStep={activeStep} sx={{ mb: 2 }}>
                  {steps.map((label, index) => (
                    <Step key={label} >
                      <StepButton color="inherit" onClick={handleStep(index)}>
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
                <Box sx={{ mt: 2, mb: 1, py: 1 }}>
                  {/* Cancel recurring session */}
                  <Box sx={activeStep === stepIndex.RECURRING_SESSION ? {} : { visibility: 'hidden', width: 0, height: 0 }}>
                    <FormControl>
                      <Typography variant="title"><b>Cancel Recurring Session</b></Typography>
                      <RadioGroup value={selectedCancelOption} onChange={(event) => { setSelectedCancelOption(event.target.value) }} sx={{ ml: 2 }}>
                        <FormControlLabel
                          control={<Radio />}
                          value={'this_session'}
                          label={'This session'}
                        />
                        <FormControlLabel
                          control={<Radio />}
                          value={'this_and_following'}
                          label={'This and following sessions'}
                        />
                        <FormControlLabel
                          control={<Radio />}
                          value={'all_sessions_until'}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography sx={{ mr: 1 }}>All sessions until</Typography>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                  <DatePicker
                                    value={endDate}
                                    disabled={selectedCancelOption !== 'all_sessions_until'}
                                    minDate={dateCellSelected?.date ? dayjs(dateCellSelected.date) : dayjs()}
                                    maxDate={maxDateForm}
                                    label={'Finish date'}
                                    onChange={(date) => setEndDate(date)}
                                    disablePast={true}
                                    format="LL"
                                    slotProps={{
                                      textField: {
                                        helperText: 'All scheduling must be in the store local date and time',
                                      },
                                    }}
                                  />
                                </DemoContainer>
                              </LocalizationProvider>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  {/* Reasons for cancel */}
                  <Box sx={activeStep === stepIndex.COMMENTS ? { my: 2 } : { visibility: 'hidden', width: 0, height: 0, top: 0, position: 'absolute' }}>
                    <ReasonForm
                      reasonError={reasonError}
                      setReasonError={setReasonError}
                      isDisabled={isDisabled}
                      modalForm={reasonForm}
                      actionReasons={actionReasons}
                      isLoadingActionReasons={isLoadingActionReasons}
                    />
                  </Box>
                  <Box sx={activeStep === stepIndex.CONFIRM_CANCELATION ? { my: 0 } : { visibility: 'hidden', width: 0, height: 0 }}>
                    <ConfirmCancelSessionForm
                      isDisabled={isDisabled}
                      selectedSessions={selectedRobots}
                      confirmRemoveSession={confirmRemoveSession}
                      setConfirmRemoveSession={setConfirmRemoveSession}
                      openWarning={openWarning}
                      setOpenWarning={setOpenWarning}
                      hasUniqueSessions={selectedSessions.findIndex(session => session.type === 'Unique') !== -1}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Divider sx={{ with: '80%!important', mb: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              <Button
                color="inherit"
                variant='outlined'
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              <Box sx={{ flex: '1 1 auto' }} />
              <Button
                color={activeStep === steps.length - 1 ? 'secondary' : 'primary'}
                disabled={handleCheckDisabledConfirm() || loadingCancelSessionResponse}
                variant='contained'
                onClick={handleNext}
              >{
                  loadingCancelSessionResponse ? <CircularProgress size={20} color='primary' /> :
                    activeStep === steps.length - 1 ? isDisabled ? 'Close' : 'Confirm'
                      : 'Next'
                }</Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog >
    </>

  );
}
